import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const WeightTagPrint = () => {
    const { id } = useParams();
    const [weightTag, setWeightTag] = useState(null);
    const [vineyard, setVineyard] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (id) {
                    const weightTagResponse = await axios.get(`/api/weightTags/${id}`);
                    setWeightTag(weightTagResponse.data);
                    const vineyardResponse = await axios.get(`/api/vineyards/${weightTagResponse.data.vineyardID}`);
                    setVineyard(vineyardResponse.data);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [id]);

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

    if (!weightTag || !vineyard) {
        return <p>Loading...</p>;
    }

    const binRows = [
        weightTag.binWeights.slice(0, 6),
        weightTag.binWeights.slice(6, 12),
        weightTag.binWeights.slice(12, 18),
    ];

    const handleDownloadPdf = () => {
        const doc = new jsPDF();
        // Add logo
        const imgURL = '/img/j-logo.png';
        doc.addImage(imgURL, 'PNG', 10, 10, 17, 28); // (image, format, x, y, width, height)
        // Header Section
        doc.setFontSize(20);
        doc.text('Jeremy Wine Co.', 33, 20);
        doc.setFontSize(12);
        doc.text('16750 HWY 88', 33, 25);
        doc.text('Lockeford, CA 95237', 33, 30);
        doc.text('209-367-3773 | BWN-16742', 33, 35);
        doc.setFontSize(16);
        doc.text('Official Weight Tag', 150, 20);

        // Separator
        doc.setLineWidth(0.5);
        doc.line(10, 40, 200, 40);

        // Details Section
        const formattedDate = weightTag?.date ? new Date(weightTag.date).toLocaleDateString() : 'N/A';
        const vineyardName = vineyard?.vineyardName || 'Unknown Vineyard';
        const varietal = weightTag?.varietal || 'Unknown Varietal';

        doc.setFontSize(14);
        doc.text(`Date: ${formattedDate}`, 10, 50);
        doc.text(`Vineyard: ${vineyardName}`, 10, 60);
        doc.text(`Varietal: ${varietal}`, 10, 70);

        // Weights Table
        const tableData = (weightTag.binWeights || []).map(bin => [
            bin?.binNumber || '-',
            bin?.grossWeight || '0',
            bin?.tareWeight || '0',
            bin?.netWeight || '0'
        ]);

        doc.autoTable({
            startY: 80,
            head: [['Bin #', 'Gross (lbs)', 'Tare (lbs)', 'Net (lbs)']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [66, 66, 66] },
            styles: { fontSize: 12 },
            margin: { top: 80 }
        });

        // Calculate the Y position after the table
        const finalY = doc.lastAutoTable.finalY + 20;

        // Totals Section with Table
        const totalGross = isNaN(Number(weightTag?.totalGross)) ? 0 : Number(weightTag.totalGross);
        const totalTare = isNaN(Number(weightTag?.totalTare)) ? 0 : Number(weightTag.totalTare);
        const totalNet = isNaN(Number(weightTag?.totalNet)) ? 0 : Number(weightTag.totalNet);
        const totalTons = isNaN(Number(weightTag?.totalTons)) ? 0 : Number(weightTag.totalTons);

        doc.autoTable({
            startY: finalY,
            head: [['Totals']],
            body: [
                ['Total Gross: ' + totalGross.toFixed(0) + ' lbs'],
                ['Total Tare: ' + totalTare.toFixed(0) + ' lbs'],
                ['Total Net: ' + totalNet.toFixed(0) + ' lbs'],
                ['Total Tons: ' + totalTons.toFixed(2) + ' tons']
            ],
            theme: 'plain',
            styles: { fontSize: 12, fontStyle: 'bold' },
            margin: { left: 10 }
        });

        // Signatures
        const signaturesY = doc.lastAutoTable.finalY + 20;
        doc.line(10, signaturesY, 90, signaturesY);
        doc.text('Grower Signature', 10, signaturesY + 10);

        doc.line(110, signaturesY, 190, signaturesY);
        doc.text('Receiver Signature', 110, signaturesY + 10);

        doc.save(`WeightTag_${formattedDate}.pdf`);
    };

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <img
                        src="http://jeremywineco.com/wp-content/themes/BOX/img/j-logo.png"
                        alt="Jeremy Wine Co"
                        style={{ height: '100px' }}
                    />
                </div>
                <div className="text-left">
                    <h2 className="mb-0">JEREMY WINE CO.</h2>
                    <p className="mb-0">16750 HWY 88</p>
                    <p className="mb-0">LOCKEFORD, CA 95237</p>
                    <p className="mb-0">209-367-3773 | BWN-16742</p>
                </div>
                <div className="text-end">
                    <h2>OFFICIAL WEIGHT TAG</h2>
                </div>
            </div>

            {/* Info Row */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <h5>DATE: {formatDate(weightTag.date)}</h5>
                </div>
                <div className="col-md-4">
                    <h5>VARIETAL: {weightTag.varietal}</h5>
                </div>
                <div className="col-md-4">
                    <h5>VINEYARD: {vineyard.vineyardName}</h5>
                </div>
            </div>

            {/* Bin Weight Tables */}
            {binRows.map((binGroup, rowIndex) => (
                <div key={rowIndex} className="mb-4">
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th style={{ width: '100px' }}>BIN #</th>
                                {binGroup.map((_, index) => (
                                    <th key={index} style={{ textAlign: 'center' }}>
                                        {rowIndex * 6 + index + 1}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>GROSS:</td>
                                {binGroup.map((bin, index) => (
                                    <td key={index} style={{ textAlign: 'center' }}>
                                        {bin.grossWeight || ''}
                                    </td>
                                ))}
                            </tr>
                            <tr>
                                <td>TARE:</td>
                                {binGroup.map((bin, index) => (
                                    <td key={index} style={{ textAlign: 'center' }}>
                                        {bin.tareWeight}
                                    </td>
                                ))}
                            </tr>
                            <tr>
                                <td>NET:</td>
                                {binGroup.map((bin, index) => (
                                    <td key={index} style={{ textAlign: 'center' }}>
                                        {bin.netWeight || ''}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ))}

            {/* Totals */}
            <div className="row justify-content-end">
                <div className="col-md-6">
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th colSpan="2" style={{ textAlign: 'center' }}>TOTALS OF WEIGHTS</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ width: '50%' }}>GROSS:</td>
                                <td style={{ textAlign: 'right' }}>{weightTag.totalGross}</td>
                            </tr>
                            <tr>
                                <td>TARE:</td>
                                <td style={{ textAlign: 'right' }}>{weightTag.totalTare}</td>
                            </tr>
                            <tr>
                                <td>NET:</td>
                                <td style={{ textAlign: 'right' }}>{weightTag.totalNet}</td>
                            </tr>
                            <tr>
                                <td>TONS:</td>
                                <td style={{ textAlign: 'right' }}>{weightTag.totalTons}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <button className="btn btn-secondary mt-3" onClick={handleDownloadPdf}>
                Download PDF
            </button>
        </div>
    );
};

export default WeightTagPrint;